// Teach once (/api/teach, U7) and the brief-only default delivery (/api/settings/brief-delivery,
// U8). Filing and narrowing only change proposals; the owner applies them on Learning.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { BriefDeliverySetting, ProposalView } from "@shared/api";
import type { DryRunView, TeachOptionsView, TeachRequest } from "@shared/teach";
import { api } from "./api";

const enc = encodeURIComponent;

export const teachKeys = {
  options: (threadKey: string) => ["teach", "options", threadKey] as const,
  dryRun: (proposalId: string, next: string) => ["teach", "dryRun", proposalId, next] as const,
  delivery: ["settings", "briefDelivery"] as const,
};

export const teachUrls = {
  options: (threadKey: string) => `/api/teach/options?threadKey=${enc(threadKey)}`,
  file: () => "/api/teach",
  dryRun: (proposalId: string) => `/api/teach/proposals/${enc(proposalId)}/dry-run`,
  refine: (proposalId: string) => `/api/teach/proposals/${enc(proposalId)}/refine`,
};

export const teachApi = {
  options: (threadKey: string, signal?: AbortSignal) =>
    api<TeachOptionsView>(teachUrls.options(threadKey), { signal }),
  file: (body: TeachRequest) => api<ProposalView>(teachUrls.file(), { method: "POST", body }),
  dryRun: (proposalId: string, signal?: AbortSignal) =>
    api<DryRunView>(teachUrls.dryRun(proposalId), { signal }),
  refine: (proposalId: string, threadKey: string) =>
    api<ProposalView>(teachUrls.refine(proposalId), { method: "POST", body: { threadKey } }),
  delivery: (signal?: AbortSignal) =>
    api<BriefDeliverySetting>("/api/settings/brief-delivery", { signal }),
  saveDelivery: (delivery: string) =>
    api<BriefDeliverySetting>("/api/settings/brief-delivery", {
      method: "PUT",
      body: { delivery },
    }),
};

export const deliveryQuery = queryOptions({
  queryKey: teachKeys.delivery,
  queryFn: ({ signal }) => teachApi.delivery(signal),
  staleTime: 5 * 60_000,
});

export function useDefaultDelivery() {
  return useQuery(deliveryQuery);
}
